import { useEffect, useState } from "react";
import { Avatar, Button, Card, Checkbox, Input, Select, StatCard, Tag } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import { useIsMobile } from "../lib/hooks";
import type { ScheduleEvent, Student } from "../lib/types";

// ── helpers ───────────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function firstOfMonth(d: Date): string {
  return toDateStr(new Date(d.getFullYear(), d.getMonth(), 1));
}

function fmt(v: number): string {
  return v % 1 === 0 ? v.toString() : v.toFixed(1);
}

const STATUS_LABEL: Record<string, string> = {
  taught: "已上课",
  leave: "请假",
  absent: "缺勤",
};

function resolveEventStatus(ev: ScheduleEvent, todayStr: string, nowTimeStr: string): ScheduleEvent["status"] {
  if (ev.status !== "upcoming") return ev.status;
  if (ev.date && ev.date < todayStr) return "taught";
  if (ev.date === todayStr && ev.end_time <= nowTimeStr) return "taught";
  return ev.status;
}

const STATUS_COLOR: Record<string, string> = {
  taught: "var(--status-taught-fg)",
  leave: "var(--status-leave-fg)",
  absent: "var(--status-absent-fg)",
};

// ── types ─────────────────────────────────────────────────────────────────

interface StudentReport {
  student: Student;
  allEvs: ScheduleEvent[];
  taughtEvs: ScheduleEvent[];
  unpaidEvs: ScheduleEvent[];
  paidEvs: ScheduleEvent[];
  leaveEvs: ScheduleEvent[];
  absentEvs: ScheduleEvent[];
  taughtHours: number;
  unpaidHours: number;
  paidHours: number;
  rate: number;
  unpaidAmount: number;
  paidAmount: number;
}

export interface ReportsProps {
  events: ScheduleEvent[];
  students: Student[];
}

// ── receipt HTML (for PDF via new window) ────────────────────────────────

function generateReceiptHTML(
  rows: StudentReport[],
  fromDate: string,
  toDate: string,
  paidEventIds: Set<number>
): string {
  const now = new Date().toLocaleString("zh-CN");
  const totalTaught = rows.reduce((s, r) => s + r.taughtHours, 0);
  const totalUnpaid = rows.reduce((s, r) => s + r.unpaidAmount, 0);
  const totalPaid = rows.reduce((s, r) => s + r.paidAmount, 0);

  const studentSections = rows.map((r) => {
    const evRows = [...r.allEvs]
      .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
      .map((ev) => {
        const isPaid = paidEventIds.has(ev.id);
        const isTaught = ev.status === "taught";
        const fee = isTaught ? `¥${(ev.hours * r.rate).toLocaleString()}` : "—";
        const payStatus = isTaught ? (isPaid ? "✓ 已收" : "待收") : "—";
        const statusCls = ev.status === "taught" ? "s-taught" : ev.status === "leave" ? "s-leave" : "s-absent";
        const payCls = isTaught ? (isPaid ? "pay-paid" : "pay-unpaid") : "pay-none";
        return `<tr>
          <td>${ev.date ?? "—"}</td>
          <td>${ev.slot}–${ev.end_time}</td>
          <td>${ev.subject}</td>
          <td>${fmt(ev.hours)}h</td>
          <td class="${statusCls}">${STATUS_LABEL[ev.status] ?? ev.status}</td>
          <td class="${isTaught && !isPaid ? "pay-unpaid" : ""}">${fee}</td>
          <td class="${payCls}">${payStatus}</td>
        </tr>`;
      }).join("");

    const subtotal = [`已上课 ${fmt(r.taughtHours)}h`];
    if (r.leaveEvs.length) subtotal.push(`请假${r.leaveEvs.length}次`);
    if (r.absentEvs.length) subtotal.push(`缺勤${r.absentEvs.length}次`);

    return `<div class="stu-section">
      <div class="stu-head">
        <strong>${r.student.name}</strong>
        <span>${r.student.grade} · ${r.student.subject} · ${r.student.type}</span>
        <span class="rate-badge">¥${r.rate}/课时</span>
      </div>
      <table class="ev-table">
        <thead><tr><th>日期</th><th>时间</th><th>科目</th><th>课时</th><th>状态</th><th>金额</th><th>缴费</th></tr></thead>
        <tbody>${evRows}</tbody>
      </table>
      <div class="subtotal">
        <span>${subtotal.join(" · ")}</span>
        ${r.unpaidEvs.length > 0 ? `<span class="pay-unpaid" style="margin-left:auto;font-weight:700;">待缴 ¥${r.unpaidAmount.toLocaleString()}</span>` : `<span style="margin-left:auto;color:#16a34a;font-weight:700;">已结清 ¥${r.paidAmount.toLocaleString()}</span>`}
      </div>
    </div>`;
  }).join("");

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<title>课时报表</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,"PingFang SC","Hiragino Sans GB",sans-serif;font-size:13px;color:#18181b;background:#fff;padding:24px;max-width:820px;margin:0 auto}
.rec-head{text-align:center;border-bottom:2px solid #18181b;padding-bottom:16px;margin-bottom:20px}
.rec-head h1{font-size:22px;letter-spacing:3px;margin-bottom:8px}
.rec-head p{font-size:12px;color:#52525b;line-height:1.8}
.stu-section{margin-bottom:18px;border:1px solid #e4e4e7;border-radius:6px;overflow:hidden}
.stu-head{display:flex;align-items:center;gap:10px;padding:10px 14px;background:#f4f4f5;border-bottom:1px solid #e4e4e7}
.stu-head strong{font-size:15px}
.stu-head span{font-size:12px;color:#52525b}
.rate-badge{margin-left:auto!important;font-weight:700;color:#1d4ed8;font-size:13px!important}
.ev-table{width:100%;border-collapse:collapse}
.ev-table th{padding:6px 12px;font-size:11px;font-weight:700;color:#71717a;background:#fafafa;border-bottom:1px solid #e4e4e7;text-align:left;white-space:nowrap}
.ev-table td{padding:7px 12px;font-size:12px;border-bottom:1px solid #f0f0f0;white-space:nowrap}
.ev-table tr:last-child td{border-bottom:none}
.s-taught{color:#16a34a;font-weight:600}
.s-leave{color:#d97706;font-weight:600}
.s-absent{color:#dc2626;font-weight:600}
.pay-unpaid{color:#dc2626;font-weight:700}
.pay-paid{color:#16a34a;font-weight:600}
.pay-none{color:#a1a1aa}
.subtotal{display:flex;align-items:center;padding:8px 14px;font-size:12px;color:#52525b;background:#fafafa;border-top:1px dashed #e4e4e7}
.totals{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;margin-top:20px;border:2px solid #18181b;border-radius:6px;overflow:hidden}
.totals-item{padding:14px;text-align:center;background:#f9f9f9}
.totals-item .lbl{font-size:11px;color:#71717a;margin-bottom:6px}
.totals-item .val{font-size:20px;font-weight:800}
.val-blue{color:#1d4ed8}
.val-red{color:#dc2626}
.val-green{color:#16a34a}
.rec-footer{text-align:center;margin-top:20px;padding-top:14px;border-top:1px solid #e4e4e7;font-size:11px;color:#a1a1aa}
@media print{
  body{padding:0;max-width:100%}
  .stu-section{page-break-inside:avoid}
  @page{margin:1.2cm;size:A4}
}
</style>
</head>
<body>
<div class="rec-head">
  <h1>课时报表</h1>
  <p>统计区间：${fromDate} — ${toDate}</p>
  <p>导出时间：${now} · 共 ${rows.length} 位学员</p>
</div>
${studentSections}
<div class="totals">
  <div class="totals-item"><div class="lbl">学员数</div><div class="val">${rows.length}位</div></div>
  <div class="totals-item"><div class="lbl">已上课时</div><div class="val val-blue">${fmt(totalTaught)}h</div></div>
  <div class="totals-item"><div class="lbl">待缴金额</div><div class="val val-red">¥${totalUnpaid.toLocaleString()}</div></div>
  <div class="totals-item"><div class="lbl">累计已收</div><div class="val val-green">¥${totalPaid.toLocaleString()}</div></div>
</div>
<div class="rec-footer">课时 · 生成于 ${now}</div>
</body>
</html>`;
}

// ── canvas receipt image export ───────────────────────────────────────────

function exportAsImage(
  rows: StudentReport[],
  fromDate: string,
  toDate: string,
  paidEventIds: Set<number>
): void {
  const W = 740;
  const PAD = 20;
  const FONT = '-apple-system, "PingFang SC", sans-serif';

  const REC_HEAD_H = 90;
  const STU_HEAD_H = 38;
  const EV_COL_H = 26;
  const EV_ROW_H = 28;
  const SUBTOTAL_H = 28;
  const STU_GAP = 14;
  const TOTALS_H = 80;
  const FOOTER_H = 36;

  // column widths: date, time, subject, hours, status, fee, pay
  const COLS = [
    { label: "日期", w: 110, align: "left" as const },
    { label: "时间", w: 110, align: "left" as const },
    { label: "科目", w: 80, align: "left" as const },
    { label: "课时", w: 56, align: "right" as const },
    { label: "状态", w: 66, align: "left" as const },
    { label: "金额", w: 90, align: "right" as const },
    { label: "缴费", w: 70, align: "left" as const },
  ];

  let H = REC_HEAD_H;
  for (const r of rows) {
    H += STU_HEAD_H + EV_COL_H + r.allEvs.length * EV_ROW_H + SUBTOTAL_H + STU_GAP;
  }
  H += TOTALS_H + FOOTER_H + 10;

  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  let y = 0;

  // ── Receipt header ──
  ctx.textAlign = "center";
  ctx.fillStyle = "#18181b";
  ctx.font = `bold 20px ${FONT}`;
  ctx.fillText("课时报表", W / 2, y + 30);
  ctx.fillStyle = "#52525b";
  ctx.font = `12px ${FONT}`;
  ctx.fillText(`统计区间：${fromDate} — ${toDate}`, W / 2, y + 52);
  ctx.fillText(`导出时间：${new Date().toLocaleString("zh-CN")} · 共 ${rows.length} 位学员`, W / 2, y + 70);
  ctx.strokeStyle = "#18181b";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(PAD, y + REC_HEAD_H - 5);
  ctx.lineTo(W - PAD, y + REC_HEAD_H - 5);
  ctx.stroke();
  y += REC_HEAD_H;

  // ── Per-student sections ──
  for (const r of rows) {
    // student header
    ctx.fillStyle = "#f4f4f5";
    ctx.fillRect(PAD, y, W - 2 * PAD, STU_HEAD_H);
    ctx.strokeStyle = "#e4e4e7";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(PAD, y, W - 2 * PAD, STU_HEAD_H);

    ctx.textAlign = "left";
    ctx.fillStyle = "#18181b";
    ctx.font = `bold 14px ${FONT}`;
    ctx.fillText(r.student.name, PAD + 12, y + 24);

    const nameW = ctx.measureText(r.student.name).width;
    ctx.fillStyle = "#52525b";
    ctx.font = `12px ${FONT}`;
    ctx.fillText(`${r.student.grade} · ${r.student.subject} · ${r.student.type}`, PAD + 12 + nameW + 10, y + 24);

    ctx.fillStyle = "#1d4ed8";
    ctx.font = `bold 13px ${FONT}`;
    ctx.textAlign = "right";
    ctx.fillText(`¥${r.rate}/课时`, W - PAD - 12, y + 24);
    ctx.textAlign = "left";
    y += STU_HEAD_H;

    // column headers
    ctx.fillStyle = "#fafafa";
    ctx.fillRect(PAD, y, W - 2 * PAD, EV_COL_H);
    ctx.strokeStyle = "#e4e4e7";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(PAD, y + EV_COL_H);
    ctx.lineTo(W - PAD, y + EV_COL_H);
    ctx.stroke();

    let cx = PAD + 12;
    for (const col of COLS) {
      ctx.fillStyle = "#71717a";
      ctx.font = `bold 11px ${FONT}`;
      if (col.align === "right") {
        ctx.textAlign = "right";
        ctx.fillText(col.label, cx + col.w - 8, y + 17);
        ctx.textAlign = "left";
      } else {
        ctx.fillText(col.label, cx, y + 17);
      }
      cx += col.w;
    }
    y += EV_COL_H;

    // event rows
    const sortedEvs = [...r.allEvs].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
    for (let i = 0; i < sortedEvs.length; i++) {
      const ev = sortedEvs[i];
      const isPaid = paidEventIds.has(ev.id);
      const isTaught = ev.status === "taught";

      ctx.fillStyle = isPaid ? "#f0fdf4" : i % 2 === 0 ? "#ffffff" : "#fafafa";
      ctx.fillRect(PAD, y, W - 2 * PAD, EV_ROW_H);

      ctx.strokeStyle = "#f0f0f0";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(PAD, y);
      ctx.lineTo(W - PAD, y);
      ctx.stroke();

      const sColor: Record<string, string> = { taught: "#16a34a", leave: "#d97706", absent: "#dc2626" };
      const cells = [
        { text: ev.date ?? "—", color: "#18181b", bold: false, col: COLS[0] },
        { text: `${ev.slot}–${ev.end_time}`, color: "#52525b", bold: false, col: COLS[1] },
        { text: ev.subject, color: "#18181b", bold: false, col: COLS[2] },
        { text: `${fmt(ev.hours)}h`, color: "#52525b", bold: false, col: COLS[3] },
        { text: STATUS_LABEL[ev.status] ?? ev.status, color: sColor[ev.status] ?? "#52525b", bold: true, col: COLS[4] },
        { text: isTaught ? `¥${(ev.hours * r.rate).toLocaleString()}` : "—", color: isTaught && !isPaid ? "#dc2626" : "#52525b", bold: isTaught && !isPaid, col: COLS[5] },
        { text: isTaught ? (isPaid ? "✓ 已收" : "待收") : "—", color: isTaught ? (isPaid ? "#16a34a" : "#dc2626") : "#a1a1aa", bold: false, col: COLS[6] },
      ];

      cx = PAD + 12;
      for (const cell of cells) {
        ctx.fillStyle = cell.color;
        ctx.font = `${cell.bold ? "bold " : ""}12px ${FONT}`;
        if (cell.col.align === "right") {
          ctx.textAlign = "right";
          ctx.fillText(cell.text, cx + cell.col.w - 8, y + EV_ROW_H / 2 + 4);
          ctx.textAlign = "left";
        } else {
          ctx.fillText(cell.text, cx, y + EV_ROW_H / 2 + 4);
        }
        cx += cell.col.w;
      }
      y += EV_ROW_H;
    }

    // subtotal bar
    ctx.fillStyle = "#f8f8f8";
    ctx.fillRect(PAD, y, W - 2 * PAD, SUBTOTAL_H);
    ctx.strokeStyle = "#e4e4e7";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(PAD, y);
    ctx.lineTo(W - PAD, y);
    ctx.stroke();

    const subtotalParts = [`小计：已上课 ${fmt(r.taughtHours)}h`];
    if (r.leaveEvs.length) subtotalParts.push(`请假${r.leaveEvs.length}次`);
    if (r.absentEvs.length) subtotalParts.push(`缺勤${r.absentEvs.length}次`);
    ctx.fillStyle = "#52525b";
    ctx.font = `12px ${FONT}`;
    ctx.textAlign = "left";
    ctx.fillText(subtotalParts.join(" · "), PAD + 12, y + 18);

    if (r.unpaidEvs.length > 0) {
      ctx.fillStyle = "#dc2626";
      ctx.font = `bold 13px ${FONT}`;
      ctx.textAlign = "right";
      ctx.fillText(`待缴 ¥${r.unpaidAmount.toLocaleString()}`, W - PAD - 12, y + 19);
      ctx.textAlign = "left";
    }

    y += SUBTOTAL_H + STU_GAP;
  }

  // ── Totals ──
  ctx.strokeStyle = "#18181b";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(PAD, y);
  ctx.lineTo(W - PAD, y);
  ctx.stroke();
  y += 10;

  ctx.fillStyle = "#f9f9f9";
  ctx.fillRect(PAD, y, W - 2 * PAD, TOTALS_H - 10);

  const totalTaught = rows.reduce((s, r) => s + r.taughtHours, 0);
  const totalUnpaid = rows.reduce((s, r) => s + r.unpaidAmount, 0);
  const totalPaid = rows.reduce((s, r) => s + r.paidAmount, 0);

  const totalsItems = [
    { label: "学员数", value: `${rows.length}位`, color: "#18181b" },
    { label: "已上课时", value: `${fmt(totalTaught)}h`, color: "#1d4ed8" },
    { label: "待缴金额", value: `¥${totalUnpaid.toLocaleString()}`, color: "#dc2626" },
    { label: "累计已收", value: `¥${totalPaid.toLocaleString()}`, color: "#16a34a" },
  ];

  const itemW = (W - 2 * PAD) / totalsItems.length;
  ctx.textAlign = "center";
  for (let i = 0; i < totalsItems.length; i++) {
    const item = totalsItems[i];
    const ix = PAD + i * itemW + itemW / 2;
    ctx.fillStyle = "#71717a";
    ctx.font = `11px ${FONT}`;
    ctx.fillText(item.label, ix, y + 18);
    ctx.fillStyle = item.color;
    ctx.font = `bold 18px ${FONT}`;
    ctx.fillText(item.value, ix, y + 44);
  }
  ctx.textAlign = "left";
  y += TOTALS_H;

  // ── Footer ──
  ctx.fillStyle = "#a1a1aa";
  ctx.font = `11px ${FONT}`;
  ctx.textAlign = "center";
  ctx.fillText(`课时管理 · 生成于 ${new Date().toLocaleString("zh-CN")}`, W / 2, y + 20);

  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `课时报表_${fromDate}_${toDate}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, "image/png");
}

// ── component ─────────────────────────────────────────────────────────────

export function Reports({ events, students }: ReportsProps) {
  const isMobile = useIsMobile();
  const now = new Date();
  const todayStr = toDateStr(now);
  const nowTimeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const effStatus = (ev: ScheduleEvent) => resolveEventStatus(ev, todayStr, nowTimeStr);

  // ── filter state ──────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState(firstOfMonth(now));
  const [toDate, setToDate] = useState(toDateStr(now));
  const [payFilter, setPayFilter] = useState<"unpaid" | "paid" | "all">("unpaid");
  const [studentFilter, setStudentFilter] = useState("");

  // ── payment (localStorage-persisted) ─────────────────────────────────────
  const [paidEventIds, setPaidEventIds] = useState<Set<number>>(() => {
    try {
      const s = localStorage.getItem("jishi_paid_events");
      return s ? new Set(JSON.parse(s) as number[]) : new Set();
    } catch { return new Set(); }
  });

  // ── expand / select ───────────────────────────────────────────────────────
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useLucideIcons([payFilter, expanded.size, selected.size]);

  useEffect(() => {
    localStorage.setItem("jishi_paid_events", JSON.stringify([...paidEventIds]));
  }, [paidEventIds]);

  // ── computed reports ──────────────────────────────────────────────────────
  const periodEvents = events.filter(
    (ev) =>
      ["taught", "leave", "absent"].includes(effStatus(ev)) &&
      ev.date &&
      ev.date >= fromDate &&
      ev.date <= toDate
  );

  const allReports: StudentReport[] = students
    .map((student) => {
      const evs = periodEvents
        .filter((e) => e.student_id === student.id || e.studentIds?.includes(student.id))
        .map((e) => ({ ...e, status: effStatus(e) }));
      if (evs.length === 0) return null;
      const taughtEvs = evs.filter((e) => e.status === "taught");
      const unpaidEvs = taughtEvs.filter((e) => !paidEventIds.has(e.id));
      const paidEvs = taughtEvs.filter((e) => paidEventIds.has(e.id));
      const leaveEvs = evs.filter((e) => e.status === "leave");
      const absentEvs = evs.filter((e) => e.status === "absent");
      const rate = student.rate ?? 100;
      const unpaidHours = unpaidEvs.reduce((s, e) => s + e.hours, 0);
      const paidHours = paidEvs.reduce((s, e) => s + e.hours, 0);
      const taughtHours = taughtEvs.reduce((s, e) => s + e.hours, 0);
      return {
        student,
        allEvs: evs,
        taughtEvs,
        unpaidEvs,
        paidEvs,
        leaveEvs,
        absentEvs,
        taughtHours,
        unpaidHours,
        paidHours,
        rate,
        unpaidAmount: Math.round(unpaidHours * rate * 10) / 10,
        paidAmount: Math.round(paidHours * rate * 10) / 10,
      };
    })
    .filter(Boolean) as StudentReport[];

  const visibleReports = allReports
    .filter((r) => !studentFilter || r.student.id === Number(studentFilter))
    .filter((r) => {
      if (payFilter === "unpaid") return r.unpaidEvs.length > 0;
      if (payFilter === "paid") return r.paidEvs.length > 0 && r.unpaidEvs.length === 0;
      return true;
    });

  const totalUnpaidHours = visibleReports.reduce((s, r) => s + r.unpaidHours, 0);
  const totalUnpaid = visibleReports.reduce((s, r) => s + r.unpaidAmount, 0);
  const totalPaid = allReports.reduce((s, r) => s + r.paidAmount, 0);
  const totalStudents = visibleReports.length;

  // ── actions ───────────────────────────────────────────────────────────────
  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const selectAll = () =>
    setSelected(
      selected.size === visibleReports.filter((r) => r.unpaidEvs.length > 0).length
        ? new Set()
        : new Set(visibleReports.filter((r) => r.unpaidEvs.length > 0).map((r) => r.student.id))
    );

  const markAsPaid = (studentId: number) => {
    const r = allReports.find((x) => x.student.id === studentId);
    if (!r) return;
    setPaidEventIds((prev) => {
      const n = new Set(prev);
      r.unpaidEvs.forEach((ev) => n.add(ev.id));
      return n;
    });
  };

  const markSelectedAsPaid = () => {
    setPaidEventIds((prev) => {
      const n = new Set(prev);
      for (const sid of selected) {
        const r = allReports.find((x) => x.student.id === sid);
        if (r) r.unpaidEvs.forEach((ev) => n.add(ev.id));
      }
      return n;
    });
    setSelected(new Set());
  };

  const exportPDF = () => {
    const html = generateReceiptHTML(visibleReports, fromDate, toDate, paidEventIds);
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  // ── styles ────────────────────────────────────────────────────────────────
  const th: React.CSSProperties = {
    padding: "11px 14px",
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text-subtle)",
    whiteSpace: "nowrap",
    borderBottom: "1px solid var(--divider)",
    background: "var(--surface-sunken)",
  };
  const td: React.CSSProperties = {
    padding: "12px 14px",
    fontSize: 13,
    color: "var(--text-body)",
    verticalAlign: "middle",
    borderTop: "1px solid var(--divider)",
    whiteSpace: "nowrap",
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Filter bar ── */}
      <Card>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "140px 140px 1fr 150px",
          gap: 10,
          alignItems: "flex-end",
        }}>
          <Input
            label="开始日期"
            type="date"
            value={fromDate}
            max={toDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <Input
            label="截止日期"
            type="date"
            value={toDate}
            min={fromDate}
            max={toDateStr(new Date())}
            onChange={(e) => setToDate(e.target.value)}
          />
          <Select
            label="学员"
            value={studentFilter}
            placeholder="全部学员"
            options={students.map((s) => ({ value: String(s.id), label: `${s.name} · ${s.grade}` }))}
            onChange={(e) => setStudentFilter(e.target.value)}
          />
          <Select
            label="缴费状态"
            value={payFilter}
            options={[
              { value: "unpaid", label: "待缴费" },
              { value: "paid", label: "已缴费" },
              { value: "all", label: "全部" },
            ]}
            onChange={(e) => setPayFilter(e.target.value as typeof payFilter)}
          />
          <div style={{ gridColumn: isMobile ? "1 / -1" : "auto", display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Icon name="printer" size={15} />}
              onClick={exportPDF}
              style={isMobile ? { flex: 1, justifyContent: "center" } : {}}
            >
              导出PDF
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Icon name="image" size={15} />}
              onClick={() => exportAsImage(visibleReports, fromDate, toDate, paidEventIds)}
              style={isMobile ? { flex: 1, justifyContent: "center" } : {}}
            >
              导出图片
            </Button>
          </div>
        </div>
      </Card>

      {/* ── KPI row ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: isMobile ? 8 : 12 }}>
        <StatCard
          label="待缴学员"
          value={totalStudents}
          unit="位"
          icon={<Icon name="users" size={17} />}
        />
        <StatCard
          label="待缴课时"
          value={fmt(totalUnpaidHours)}
          unit="h"
          icon={<Icon name="clock" size={17} />}
        />
        <StatCard
          label="待缴金额"
          value={"¥" + totalUnpaid.toLocaleString()}
          icon={<Icon name="circle-alert" size={17} />}
          style={{ borderColor: totalUnpaid > 0 ? "var(--danger-bg)" : undefined }}
        />
        <StatCard
          label="累计已收"
          value={"¥" + totalPaid.toLocaleString()}
          icon={<Icon name="circle-check" size={17} />}
          accent="accent"
        />
      </div>

      {/* ── Batch action bar ── */}
      {selected.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 16px",
            background: "var(--brand-subtle)",
            border: "1.5px solid var(--border-brand, var(--brand))",
            borderRadius: "var(--radius-md)",
          }}
        >
          <Icon name="check-square" size={16} />
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
            已选 {selected.size} 位学员，待缴合计 ¥
            {allReports
              .filter((r) => selected.has(r.student.id))
              .reduce((s, r) => s + r.unpaidAmount, 0)
              .toLocaleString()}
          </span>
          <Button variant="primary" size="sm" onClick={markSelectedAsPaid}>
            批量标记已缴费
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            取消选择
          </Button>
        </div>
      )}

      {/* ── Mobile card list ── */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleReports.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--text-subtle)", padding: "40px 0", fontSize: 14 }}>
              {payFilter === "unpaid" ? "暂无待缴费课时" : payFilter === "paid" ? "暂无已缴费记录" : "该时间段内暂无课时记录"}
            </div>
          )}
          {visibleReports.map((r) => (
            <div
              key={r.student.id}
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--divider)",
                borderRadius: "var(--radius-md)",
                overflow: "hidden",
              }}
            >
              {/* Header row */}
              <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                {r.unpaidEvs.length > 0 && (
                  <Checkbox checked={selected.has(r.student.id)} onChange={() => toggleSelect(r.student.id)} />
                )}
                <Avatar name={r.student.name} size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: "var(--text-strong)", fontSize: 15 }}>{r.student.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-subtle)" }}>{r.student.phone || r.student.grade}</div>
                </div>
                {r.unpaidEvs.length > 0 ? (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 99, whiteSpace: "nowrap",
                    background: "var(--status-absent-bg)", color: "var(--status-absent-fg)",
                  }}>待缴费</span>
                ) : (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 99, whiteSpace: "nowrap",
                    background: "var(--status-taught-bg)", color: "var(--status-taught-fg)",
                  }}>已缴费</span>
                )}
              </div>

              {/* Tags + rate */}
              <div style={{
                padding: "8px 14px", background: "var(--surface-sunken)",
                borderTop: "1px solid var(--divider)",
                display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
              }}>
                <Tag tone="brand">{r.student.grade}</Tag>
                <Tag>{r.student.subject}</Tag>
                <Tag>{r.student.type}</Tag>
                <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-subtle)", fontFamily: "var(--font-num)", fontWeight: 600 }}>
                  ¥{r.rate}/课时
                </span>
              </div>

              {/* Stats row */}
              <div style={{
                padding: "10px 14px", borderTop: "1px solid var(--divider)",
                display: "flex", gap: 0,
              }}>
                {[
                  { label: "已上课时", value: fmt(r.taughtHours) + "h", color: "var(--status-taught-fg)" },
                  ...(r.leaveEvs.length ? [{ label: "请假", value: r.leaveEvs.length + "次", color: "var(--status-leave-fg)" }] : []),
                  ...(r.absentEvs.length ? [{ label: "缺勤", value: r.absentEvs.length + "次", color: "var(--status-absent-fg)" }] : []),
                ].map((item) => (
                  <div key={item.label} style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: item.color, fontFamily: "var(--font-num)", lineHeight: 1.2 }}>
                      {item.value}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-subtle)", marginTop: 2 }}>{item.label}</div>
                  </div>
                ))}
              </div>

              {/* Action row */}
              <div style={{
                padding: "10px 14px", borderTop: "1px solid var(--divider)",
                display: "flex", alignItems: "center", gap: 10,
              }}>
                {r.unpaidEvs.length > 0 ? (
                  <div>
                    <span style={{ fontSize: 12, color: "var(--text-subtle)" }}>待缴</span>
                    <span style={{ fontSize: 20, fontWeight: 900, color: "var(--danger-fg)", fontFamily: "var(--font-num)", marginLeft: 6 }}>
                      ¥{r.unpaidAmount.toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: "var(--status-taught-fg)", fontWeight: 700 }}>✓ 已结清</span>
                )}
                <div style={{ flex: 1 }} />
                {r.unpaidEvs.length > 0 && (
                  <Button variant="primary" size="sm" onClick={() => markAsPaid(r.student.id)}>
                    标记已缴
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => toggleExpand(r.student.id)}
                  style={{
                    border: "none", background: "transparent", cursor: "pointer",
                    color: "var(--text-muted)", padding: 4, borderRadius: "var(--radius-sm)",
                    display: "flex", alignItems: "center",
                    transition: "transform 0.15s",
                    transform: expanded.has(r.student.id) ? "rotate(90deg)" : "none",
                  }}
                  aria-label="展开明细"
                >
                  <Icon name="chevron-right" size={18} />
                </button>
              </div>

              {/* Expandable lesson list */}
              {expanded.has(r.student.id) && (
                <div style={{ borderTop: "1px solid var(--divider)" }}>
                  {[...r.allEvs].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? "")).map((ev, i, arr) => {
                    const isPaid = paidEventIds.has(ev.id);
                    const isTaught = ev.status === "taught";
                    return (
                      <div
                        key={ev.id}
                        style={{
                          padding: "10px 14px",
                          background: isPaid ? "var(--status-taught-bg)" : i % 2 === 0 ? "var(--surface-sunken)" : "transparent",
                          borderBottom: i < arr.length - 1 ? "1px solid var(--divider)" : "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-strong)", fontFamily: "var(--font-num)" }}>
                            {ev.date ?? "—"}
                          </span>
                          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                            {ev.slot}–{ev.end_time}
                          </span>
                          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: STATUS_COLOR[ev.status] ?? "var(--text-muted)" }}>
                            {STATUS_LABEL[ev.status] ?? ev.status}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, color: "var(--text-subtle)" }}>{ev.subject}</span>
                          <span style={{ fontSize: 12, color: "var(--text-subtle)" }}>· {fmt(ev.hours)}h</span>
                          {isTaught && (
                            <>
                              <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: !isPaid ? "var(--danger-fg)" : "var(--text-muted)" }}>
                                ¥{(ev.hours * r.rate).toLocaleString()}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isPaid ? "var(--status-taught-fg)" : "var(--status-absent-fg)" }}>
                                {isPaid ? "✓ 已收" : "待收"}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Mobile totals footer */}
          {visibleReports.length > 0 && (
            <div style={{
              padding: "12px 16px",
              background: "var(--brand-subtle)",
              border: "1px solid var(--divider)",
              borderRadius: "var(--radius-md)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 8,
            }}>
              <span style={{ fontSize: 13, color: "var(--text-subtle)" }}>共 {visibleReports.length} 位学员</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "var(--danger-fg)", fontFamily: "var(--font-num)" }}>
                待缴 ¥{totalUnpaid.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      ) : (

      /* ── Desktop table ── */
      <Card pad={false} style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 36, paddingRight: 0 }}>
                <Checkbox
                  checked={selected.size > 0 && selected.size === visibleReports.filter((r) => r.unpaidEvs.length > 0).length}
                  onChange={selectAll}
                />
              </th>
              <th style={{ ...th, textAlign: "left" }}>学员</th>
              <th style={{ ...th, textAlign: "left" }}>年级 · 科目</th>
              <th style={{ ...th, textAlign: "right" }}>课时费/h</th>
              <th style={{ ...th, textAlign: "right" }}>已上课时</th>
              <th style={{ ...th, textAlign: "right" }}>请假</th>
              <th style={{ ...th, textAlign: "right" }}>缺勤</th>
              <th style={{ ...th, textAlign: "right" }}>待缴金额</th>
              <th style={{ ...th, textAlign: "center" }}>缴费状态</th>
              <th style={{ ...th, textAlign: "right" }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleReports.length === 0 && (
              <tr>
                <td colSpan={10} style={{ ...td, textAlign: "center", color: "var(--text-subtle)", padding: "40px 14px" }}>
                  {payFilter === "unpaid" ? "暂无待缴费课时" : payFilter === "paid" ? "暂无已缴费记录" : "该时间段内暂无课时记录"}
                </td>
              </tr>
            )}
            {visibleReports.map((r) => (
              <>
                <tr
                  key={r.student.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleExpand(r.student.id)}
                >
                  <td style={{ ...td, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
                    {r.unpaidEvs.length > 0 && (
                      <Checkbox
                        checked={selected.has(r.student.id)}
                        onChange={() => toggleSelect(r.student.id)}
                      />
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={r.student.name} size="sm" />
                      <div>
                        <div style={{ fontWeight: 700, color: "var(--text-strong)" }}>{r.student.name}</div>
                        <div style={{ fontSize: 11, color: "var(--text-subtle)" }}>{r.student.phone || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 5 }}>
                      <Tag tone="brand">{r.student.grade}</Tag>
                      <Tag>{r.student.subject}</Tag>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right", color: "var(--text-subtle)", fontFamily: "var(--font-num)" }}>
                    ¥{r.rate}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "var(--status-taught-fg)" }}>
                    {fmt(r.taughtHours)}h
                  </td>
                  <td style={{ ...td, textAlign: "right", color: r.leaveEvs.length ? "var(--status-leave-fg)" : "var(--text-subtle)" }}>
                    {r.leaveEvs.length ? `${r.leaveEvs.length}次` : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: r.absentEvs.length ? "var(--status-absent-fg)" : "var(--text-subtle)" }}>
                    {r.absentEvs.length ? `${r.absentEvs.length}次` : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {r.unpaidEvs.length > 0 ? (
                      <span style={{ fontWeight: 800, fontFamily: "var(--font-num)", color: "var(--danger-fg)", fontSize: 15 }}>
                        ¥{r.unpaidAmount.toLocaleString()}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-subtle)", fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {r.unpaidEvs.length > 0 ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700,
                        background: "var(--status-absent-bg)", color: "var(--status-absent-fg)",
                      }}>
                        <Icon name="clock" size={11} /> 待缴费
                      </span>
                    ) : (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700,
                        background: "var(--status-taught-bg)", color: "var(--status-taught-fg)",
                      }}>
                        <Icon name="check" size={11} /> 已缴费
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {r.unpaidEvs.length > 0 && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); markAsPaid(r.student.id); }}
                        >
                          标记已缴
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleExpand(r.student.id); }}
                        style={{
                          border: "none", background: "transparent", cursor: "pointer",
                          color: "var(--text-muted)", padding: "4px", borderRadius: "var(--radius-sm)",
                          transition: "transform 0.15s",
                          transform: expanded.has(r.student.id) ? "rotate(90deg)" : "none",
                        }}
                        aria-label="展开明细"
                      >
                        <Icon name="chevron-right" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>

                {/* ── Expandable detail rows ── */}
                {expanded.has(r.student.id) && (
                  <tr key={`${r.student.id}-detail`}>
                    <td colSpan={10} style={{ padding: 0, background: "var(--surface-sunken)" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {["日期", "时间", "科目·课型", "课时", "状态", "金额", "缴费"].map((h) => (
                              <th
                                key={h}
                                style={{
                                  padding: "8px 20px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--text-subtle)",
                                  textAlign: "left",
                                  whiteSpace: "nowrap",
                                  borderBottom: "1px solid var(--divider)",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...r.allEvs]
                            .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
                            .map((ev) => {
                              const isPaid = paidEventIds.has(ev.id);
                              const isTaught = ev.status === "taught";
                              return (
                                <tr key={ev.id} style={{ background: isPaid ? "var(--status-taught-bg)" : undefined }}>
                                  <td style={{ padding: "7px 20px", fontSize: 12, color: "var(--text-body)", fontVariantNumeric: "tabular-nums" }}>
                                    {ev.date ?? "—"}
                                  </td>
                                  <td style={{ padding: "7px 20px", fontSize: 12, color: "var(--text-muted)" }}>
                                    {ev.slot}–{ev.end_time}
                                  </td>
                                  <td style={{ padding: "7px 20px", fontSize: 12, color: "var(--text-body)" }}>
                                    {ev.subject}
                                  </td>
                                  <td style={{ padding: "7px 20px", fontSize: 12, color: "var(--text-muted)" }}>
                                    {fmt(ev.hours)}h
                                  </td>
                                  <td style={{ padding: "7px 20px", fontSize: 12, color: STATUS_COLOR[ev.status] ?? "var(--text-muted)", fontWeight: 600 }}>
                                    {STATUS_LABEL[ev.status] ?? ev.status}
                                  </td>
                                  <td style={{ padding: "7px 20px", fontSize: 12, fontWeight: 700, color: isTaught && !isPaid ? "var(--danger-fg)" : "var(--text-muted)" }}>
                                    {isTaught ? `¥${(ev.hours * r.rate).toLocaleString()}` : "—"}
                                  </td>
                                  <td style={{ padding: "7px 20px", fontSize: 12 }}>
                                    {isTaught ? (
                                      isPaid ? (
                                        <span style={{ color: "var(--status-taught-fg)", fontWeight: 600 }}>✓ 已收</span>
                                      ) : (
                                        <span style={{ color: "var(--status-absent-fg)", fontWeight: 600 }}>待收</span>
                                      )
                                    ) : (
                                      <span style={{ color: "var(--text-subtle)" }}>—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>

          {/* ── Footer totals ── */}
          {visibleReports.length > 0 && (
            <tfoot>
              <tr style={{ background: "var(--surface-brand-soft, var(--brand-subtle))" }}>
                <td colSpan={4} style={{ ...td, fontWeight: 800, color: "var(--text-strong)", borderTop: "1.5px solid var(--border-brand, var(--brand))" }} />
                <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "var(--status-taught-fg)", borderTop: "1.5px solid var(--border-brand, var(--brand))" }}>
                  {fmt(visibleReports.reduce((s, r) => s + r.taughtHours, 0))}h
                </td>
                <td style={{ ...td, textAlign: "right", fontWeight: 800, borderTop: "1.5px solid var(--border-brand, var(--brand))" }}>
                  {visibleReports.reduce((s, r) => s + r.leaveEvs.length, 0) || "—"}
                </td>
                <td style={{ ...td, textAlign: "right", fontWeight: 800, borderTop: "1.5px solid var(--border-brand, var(--brand))" }}>
                  {visibleReports.reduce((s, r) => s + r.absentEvs.length, 0) || "—"}
                </td>
                <td style={{ ...td, textAlign: "right", fontWeight: 900, color: "var(--brand)", fontFamily: "var(--font-num)", fontSize: 16, borderTop: "1.5px solid var(--border-brand, var(--brand))" }}>
                  ¥{totalUnpaid.toLocaleString()}
                </td>
                <td colSpan={2} style={{ ...td, textAlign: "center", fontWeight: 700, color: "var(--text-muted)", borderTop: "1.5px solid var(--border-brand, var(--brand))" }}>
                  共 {visibleReports.length} 位学员
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
      )}

    </div>
  );
}
